import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';

const ROLES = ['patient', 'doctor', 'retailer', 'clinic', 'hospital'];

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    duration_days: '30',
    chat_limit: '5',
    voice_limit: '2',
    video_limit: '1',
    is_active: true,
    target_role: 'patient',
    ios_amount: '',
    appleProductId: ''
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      amount: '',
      duration_days: '30',
      chat_limit: '5',
      voice_limit: '2',
      video_limit: '1',
      is_active: true,
      target_role: 'patient',
      ios_amount: '',
      appleProductId: ''
    });
    setEditingPlan(null);
    setShowForm(false);
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await adminAPI.getSubscriptionPlans();
      if (res.data?.success) {
        setPlans(res.data.data || []);
      }
    } catch (error) {
      showError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || '',
      description: plan.description || '',
      amount: String(plan.amount || ''),
      duration_days: String(plan.duration_days || '30'),
      chat_limit: String(plan.chat_limit ?? '5'),
      voice_limit: String(plan.voice_limit ?? '2'),
      video_limit: String(plan.video_limit ?? '1'),
      is_active: plan.is_active ?? true,
      target_role: plan.target_role || 'patient',
      ios_amount: String(plan.ios_amount || ''),
      appleProductId: plan.appleProductId || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (planId, planName) => {
    if (!window.confirm(`Delete plan "${planName}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteSubscriptionPlan(planId);
      showSuccess('Plan deleted');
      fetchPlans();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to delete plan');
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await adminAPI.updateSubscriptionPlan(plan._id, { is_active: !plan.is_active });
      showSuccess(plan.is_active ? 'Plan deactivated' : 'Plan activated');
      fetchPlans();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update plan');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        amount: parseFloat(formData.amount),
        duration_days: parseInt(formData.duration_days, 10),
        chat_limit: parseInt(formData.chat_limit, 10),
        voice_limit: parseInt(formData.voice_limit, 10),
        video_limit: parseInt(formData.video_limit, 10),
        is_active: formData.is_active,
        target_role: formData.target_role,
        appleProductId: formData.appleProductId.trim()
      };

      if (editingPlan) {
        await adminAPI.updateSubscriptionPlan(editingPlan._id, payload);
        showSuccess('Plan updated');
      } else {
        await adminAPI.createSubscriptionPlan(payload);
        showSuccess('Plan created');
      }

      resetForm();
      fetchPlans();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save plan');
    }
  };

  const filteredPlans = roleFilter === 'all'
    ? plans
    : plans.filter(p => p.target_role === roleFilter);

  if (loading) {
    return <div className="text-center py-8">Loading plans...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gold">Subscription Plans</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="bg-gray-700 text-light py-2 px-4 rounded-lg hover:bg-gray-600"
          >
            Back
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="bg-gold text-dark py-2 px-4 rounded-lg font-medium hover:opacity-90"
          >
            {showForm ? 'Cancel' : 'Add Plan'}
          </button>
        </div>
      </div>

      <div className="bg-blue-900/30 border border-blue-500/50 rounded-xl p-4 text-blue-200 text-sm">
        <h3 className="font-bold text-blue-400 mb-1">iOS App Bundle ID: com.vivara.healthcare</h3>
        <p>Ensure Apple Product IDs match what is configured in App Store Connect exactly. Examples:</p>
        <ul className="list-disc pl-5 mt-1 opacity-80 grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <li>com.vivara.healthcare.patient.basic.monthlyv2</li>
          <li>com.vivara.healthcare.patient.premium.monthlyv2</li>
          <li>com.vivara.healthcare.doctor.6monthv2</li>
          <li>com.vivara.healthcare.doctor.yearlyv2</li>
          <li>com.vivara.healthcare.retailer.6monthv2</li>
          <li>com.vivara.healthcare.clinic.yearlyv2</li>
          <li>com.vivara.healthcare.hospital.yearlyv2</li>
        </ul>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gold">{editingPlan ? 'Edit' : 'New'} Subscription Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
                required
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Target Role *</label>
              <select
                name="target_role"
                value={formData.target_role}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
              >
                <option value="patient">Patient</option>
                <option value="doctor">Doctor</option>
                <option value="retailer">Retailer</option>
                <option value="clinic">Clinic</option>
                <option value="hospital">Hospital</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Android/Web Price (₹) *</label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                min="0"
                step="1"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
                required
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">iOS Price (Fallback) (₹)</label>
              <input
                type="number"
                name="ios_amount"
                value={formData.ios_amount}
                onChange={handleChange}
                min="0"
                step="1"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-1">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-1">Apple Product ID (for iOS In-App Purchase)</label>
              <input
                type="text"
                name="appleProductId"
                value={formData.appleProductId}
                onChange={handleChange}
                placeholder="e.g. com.vivara.healthcare.patient.basic.monthlyv2"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Duration (days)</label>
              <input
                type="number"
                name="duration_days"
                value={formData.duration_days}
                onChange={handleChange}
                min="1"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
              />
            </div>
            {formData.target_role === 'patient' && (
              <>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Chat limit</label>
                  <input
                    type="number"
                    name="chat_limit"
                    value={formData.chat_limit}
                    onChange={handleChange}
                    min="0"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Voice limit</label>
                  <input
                    type="number"
                    name="voice_limit"
                    value={formData.voice_limit}
                    onChange={handleChange}
                    min="0"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Video limit</label>
                  <input
                    type="number"
                    name="video_limit"
                    value={formData.video_limit}
                    onChange={handleChange}
                    min="0"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-light"
                  />
                </div>
              </>
            )}
            <div className="flex items-center">
              <label className="flex items-center gap-2 text-gray-400">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="rounded"
                />
                Active (visible to patients)
              </label>
            </div>
          </div>
          <button type="submit" className="bg-gold text-dark py-2 px-4 rounded-lg font-medium">
            {editingPlan ? 'Update Plan' : 'Create Plan'}
          </button>
        </form>
      )}

      <div className="flex gap-2 mb-2">
        <button
          onClick={() => setRoleFilter('all')}
          className={`px-3 py-1 rounded-lg text-sm font-medium ${roleFilter === 'all' ? 'bg-gold text-dark' : 'bg-gray-700 text-light'}`}
        >
          All
        </button>
        {ROLES.map(role => (
          <button
            key={role}
            onClick={() => setRoleFilter(role)}
            className={`px-3 py-1 rounded-lg text-sm font-medium capitalize ${roleFilter === role ? 'bg-gold text-dark' : 'bg-gray-700 text-light'}`}
          >
            {role}
          </button>
        ))}
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4">
          Plans {roleFilter !== 'all' && `(${roleFilter})`}
        </h2>
        {filteredPlans.length === 0 ? (
          <p className="text-gray-400">No plans found.</p>
        ) : (
          <div className="space-y-4">
            {filteredPlans.map((plan) => (
              <div
                key={plan._id}
                className="flex flex-wrap justify-between items-center gap-4 p-4 bg-gray-700 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-light truncate">
                    {plan.name}
                    <span className="ml-2 text-xs bg-gray-600 px-2 py-0.5 rounded text-gray-300 border border-gray-500">
                      {plan.target_role?.toUpperCase() || 'PATIENT'}
                    </span>
                  </h3>
                  {plan.description && (
                    <p className="text-gray-400 text-sm truncate">{plan.description}</p>
                  )}
                  <p className="text-gold font-medium mt-1">Android/Web: ₹{plan.amount} {plan.ios_amount ? `• iOS: ₹${plan.ios_amount}` : ''} • {plan.duration_days} days</p>
                  {plan.appleProductId && (
                    <p className="text-blue-400 text-xs mt-1 font-mono">
                      Apple ID: {plan.appleProductId}
                    </p>
                  )}
                  {plan.target_role === 'patient' && (
                    <p className="text-gray-500 text-xs mt-1">
                      Chat: {plan.chat_limit ?? 0} • Voice: {plan.voice_limit ?? 0} • Video: {plan.video_limit ?? 0}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`px-2 py-1 rounded text-xs ${plan.is_active ? 'bg-green-600' : 'bg-gray-600'}`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => handleToggleActive(plan)}
                    className="text-xs bg-gray-600 hover:bg-gray-500 text-light px-2 py-1 rounded"
                    title="Toggle active"
                  >
                    {plan.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleEdit(plan)}
                    className="text-xs bg-blue-600 hover:bg-blue-500 text-light px-2 py-1 rounded"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(plan._id, plan.name)}
                    className="text-xs bg-red-600 hover:bg-red-500 text-light px-2 py-1 rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPlans;
